/* eslint-disable */
// ─────────────────────────────────────────────────────────────────
// CHESS LESSON 2 — The Chess Pieces: Names and Letters
// 58 minutes · Tutor: Ms. Momo | Beginner Module · Lesson 2 of 24
// Builds on Lesson 1 (board, files, ranks, squares, piece shapes)
//
// CONCEPT (from Super Curriculum): Each chess piece has a unique
// letter used in chess notation. Knowing piece letters unlocks the
// ability to read and discuss moves in Lessons 3 onward.
// OBJECTIVE: Student knows every piece's name, letter, starting
// square(s), and can read a basic move like "Rook to e4 = Re4".
//
// CURRICULUM DECISION: Full algebraic notation (captures "x",
// check "+", reading game scores, writing exercises, notation
// puzzle) moved to Lesson 24 — after students have seen all pieces
// move and experienced captures/checks in Lessons 7-10.
// Only the MINIMUM notation (piece letter + square) stays here,
// because Lessons 3-5 refer to pieces by letter ("R for Rook").
//
// VISUAL RULE (applies to every step in this file): any spoken
// move or piece reference must be shown — on the board via FEN/
// demoSequence/path dots, and on the right panel via moveNotation/
// moveExplain or pieceLetterRef. No move is ever spoken without
// being seen.
// ─────────────────────────────────────────────────────────────────

export const LESSON_2 = {
  id: 'chess-lesson-2',
  title: 'The Chess Pieces — Names and Letters',
  subtitle: 'Lesson 2 of 24 · Beginner Module',
  totalMinutes: 58,
  doneMsg: 'You learned piece names, piece letters (K Q R B N), how to read a chess move, and where every piece starts on the board.',
  doneVoice: '{name}, you were absolutely BRILLIANT today! You know every piece by name and letter, and you can read chess moves like a pro. See you in Lesson 3!',
  nextLessonTitle: 'Lesson 3 — How the Rook and Bishop Move',
  nextLessonPreview: 'Next time you discover how the pieces actually move! The Rook slides in straight lines, the Bishop glides on diagonals — and you will make your first real moves on the board.',

  phases: [

    // ═══════════════════════════════════════════════════════════
    // PHASE 1 — WARM-UP (5 minutes) — square recall from Lesson 1
    // ═══════════════════════════════════════════════════════════
    {
      id: 'warmup',
      title: 'Warm-up — Square Recall',
      type: 'warmup',
      durationMins: 5,
      steps: [
        {
          id: 'wa1',
          type: 'observe',
          boardState: 'start',
          highlights: [],
          voice: `Welcome back, {name}! Last time you met the whole chess army — King, Queen, Rook, Bishop, Knight, and Pawn. Today we learn each piece's special LETTER, so that when we talk about chess moves, you know exactly which piece we mean. But first — let us warm up your memory with the squares you learned. I am going to CALL OUT square names and you must find them on the board. NO glowing hints this time! Say each name out loud first, then click. Ready?`,
          task: null,
          taskType: 'observe',
          continueLabel: "Let's warm up!",
        },
        {
          id: 'wa2',
          type: 'independent-squares',
          boardState: 'empty',
          highlights: [],
          voice: `Find this square: e4!`,
          task: 'Click the squares Ms. Momo calls out.',
          taskType: 'independent-squares',
          targetSquares: ['e4', 'a1', 'h8', 'd5', 'b3', 'g6'],
          voiceCorrect: [
            'e4 — the most famous square in chess!',
            'a1 — the dark corner, well done!',
            'h8 — the light corner, excellent!',
            'd5 — right in the centre!',
            'b3 — great recall, {name}!',
            'g6 — you remembered everything!',
          ],
          voiceWrong: `Not quite — remember, file letter first, then rank number. Find {sq}!`,
          successVoice: `{score} out of 6 — your board memory is sharp, {name}! Now let us learn each piece's letter!`,
        },
      ],
    },

    // ═══════════════════════════════════════════════════════════
    // PHASE 2 — PIECE LETTERS: INTRO (5 minutes)
    // ═══════════════════════════════════════════════════════════
    {
      id: 'letterintro',
      title: 'Why Pieces Have Letters',
      type: 'teach',
      durationMins: 5,
      steps: [
        {
          id: 'li1',
          type: 'observe',
          boardState: 'start',
          highlights: [],
          voice: `{name}, imagine you want to tell a friend which piece you just moved, without showing them the board. You need a SHORT code — a single letter for each piece. Chess players all over the world use the SAME letters, so a game written down in Nigeria can be read by a player in Japan or Brazil! The code is simple. Each of the six piece types has one letter — except the Pawn, which has NO letter at all. Let me show you each one!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'Show me the letters!',
        },
        {
          id: 'li2',
          type: 'observe',
          boardState: 'start',
          highlights: [],
          pieceLetterRef: [
            { icon: '♔', letter: 'K', name: 'King' },
            { icon: '♕', letter: 'Q', name: 'Queen' },
            { icon: '♖', letter: 'R', name: 'Rook' },
            { icon: '♗', letter: 'B', name: 'Bishop' },
            { icon: '♘', letter: 'N', name: 'Knight' },
          ],
          voice: `Here are five of the six letters: K for King. Q for Queen. R for Rook. B for Bishop. And N for Knight — NOT K, because King already took K! Five pieces, five letters — K, Q, R, B, N. Say them with me! Now — the sixth piece is special. It breaks the rule entirely!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'What about the Pawn?',
        },
        {
          id: 'li2b',
          type: 'observe',
          boardState: '8/pppppppp/8/8/8/8/PPPPPPPP/8 w - - 0 1',
          highlights: [],
          highlightRanks: [2, 7],
          pieceLetterRef: [
            { icon: '♔', letter: 'K', name: 'King' },
            { icon: '♕', letter: 'Q', name: 'Queen' },
            { icon: '♖', letter: 'R', name: 'Rook' },
            { icon: '♗', letter: 'B', name: 'Bishop' },
            { icon: '♘', letter: 'N', name: 'Knight' },
            { icon: '♙', letter: '—', name: 'Pawn (no letter)' },
          ],
          voice: `The PAWN has NO letter at all! If you see a move with NO capital letter at the front — like "e4" or "d5" — it is ALWAYS a Pawn. No letter equals Pawn. The Pawns stand on rank 2 for White and rank 7 for Black. They are the only piece without a letter. So the full set is: K, Q, R, B, N — and Pawns have NONE. Now let us meet each piece one by one!`,
          task: null,
          taskType: 'observe',
          continueLabel: "Let's meet each piece!",
        },
      ],
    },

    // ═══════════════════════════════════════════════════════════
    // PHASE 3 — EACH PIECE IN DETAIL (15 minutes)
    // One step per piece — isolated FEN so ONLY that piece shows
    // Progressive pieceLetterRef builds visual memory as we go
    // ═══════════════════════════════════════════════════════════
    {
      id: 'pieceletters',
      title: 'Each Piece and Its Letter',
      type: 'teach',
      durationMins: 15,
      steps: [
        {
          id: 'pl1',
          type: 'observe',
          boardState: '4k3/8/8/8/8/8/8/4K3 w - - 0 1',
          highlights: ['e1', 'e8'],
          pieceLetterRef: [{ icon: '♔', letter: 'K', name: 'King' }],
          voice: `First — the KING. Look at e1 for White and e8 for Black — only the Kings on the board, so you can focus on them clearly. The King's letter is K. Simple! Whenever you see a capital K in a chess move, the King moved. The King is the most important piece — if your King is trapped, the game is over. His letter K is easy to remember — K for King!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'Next — the Queen',
        },
        {
          id: 'pl2',
          type: 'observe',
          boardState: '3q4/8/8/8/8/8/8/3Q4 w - - 0 1',
          highlights: ['d1', 'd8'],
          pieceLetterRef: [
            { icon: '♔', letter: 'K', name: 'King' },
            { icon: '♕', letter: 'Q', name: 'Queen' },
          ],
          voice: `Next — the QUEEN. Her letter is Q. See her on d1 for White and d8 for Black. Here is a memory trick: the Queen always starts on her OWN colour. White Queen on d1 — is d1 light or dark? Think back to Lesson 1! It is LIGHT — and the White Queen wears light! Q for Queen — the most powerful piece on the board.`,
          task: null,
          taskType: 'observe',
          continueLabel: 'Next — the Rook',
        },
        {
          id: 'pl3',
          type: 'observe',
          boardState: 'r6r/8/8/8/8/8/8/R6R w - - 0 1',
          highlights: ['a1', 'h1'],
          pieceLetterRef: [
            { icon: '♔', letter: 'K', name: 'King' },
            { icon: '♕', letter: 'Q', name: 'Queen' },
            { icon: '♖', letter: 'R', name: 'Rook' },
          ],
          voice: `Now the ROOKS — the castle towers! Their letter is R. White's Rooks start in the corners, a1 and h1. They are symmetric — one on each side of the King. R for Rook — easy to remember because Rook starts with R! They will move in straight lines, but we will learn that properly in our very next lesson.`,
          task: null,
          taskType: 'observe',
          continueLabel: 'Next — the Bishop',
        },
        {
          id: 'pl4',
          type: 'observe',
          boardState: '2b2b2/8/8/8/8/8/8/2B2B2 w - - 0 1',
          highlights: ['c1', 'f1'],
          pieceLetterRef: [
            { icon: '♔', letter: 'K', name: 'King' },
            { icon: '♕', letter: 'Q', name: 'Queen' },
            { icon: '♖', letter: 'R', name: 'Rook' },
            { icon: '♗', letter: 'B', name: 'Bishop' },
          ],
          voice: `The BISHOPS — on c1 and f1 for White. Their letter is B. Notice something already: one Bishop is on c1, a dark square, and the other is on f1, a light square. They will STAY on those colours for the whole game! That is the Bishop's big secret, which we also explore in Lesson 3. B for Bishop — both words start with B!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'Next — the Knight',
        },
        {
          id: 'pl5',
          type: 'observe',
          boardState: '1n4n1/8/8/8/8/8/8/1N4N1 w - - 0 1',
          highlights: ['b1', 'g1'],
          pieceLetterRef: [
            { icon: '♔', letter: 'K', name: 'King' },
            { icon: '♕', letter: 'Q', name: 'Queen' },
            { icon: '♖', letter: 'R', name: 'Rook' },
            { icon: '♗', letter: 'B', name: 'Bishop' },
            { icon: '♘', letter: 'N', name: 'Knight' },
          ],
          voice: `The KNIGHTS — on b1 and g1. Here is the tricky one: the Knight's letter is N — NOT K! Why? Because the King ALREADY took K. So we use N — think of "k-NIGHT" and write the N you hear in the middle. The Knight is the only piece that can JUMP over other pieces — it moves in an L-shape. We learn that jump properly in Lesson 5. N for kNight!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'Last — the Pawn',
        },
        {
          id: 'pl6',
          type: 'observe',
          boardState: '8/pppppppp/8/8/8/8/PPPPPPPP/8 w - - 0 1',
          highlights: [],
          highlightRanks: [2, 7],
          pieceLetterRef: [
            { icon: '♔', letter: 'K', name: 'King' },
            { icon: '♕', letter: 'Q', name: 'Queen' },
            { icon: '♖', letter: 'R', name: 'Rook' },
            { icon: '♗', letter: 'B', name: 'Bishop' },
            { icon: '♘', letter: 'N', name: 'Knight' },
            { icon: '♙', letter: '—', name: 'Pawn (no letter)' },
          ],
          voice: `And finally — the eight PAWNS! They stand in a row across rank 2 for White and rank 7 for Black. Pawns have NO letter at all — they are the only piece without one. So in chess notation, if you see a move like "e4" with NO capital letter at the start, it ALWAYS means a Pawn moved. No letter equals Pawn — remember that! Pawns may look small but they can become very powerful. We'll see why in Lesson 5!`,
          task: null,
          taskType: 'observe',
          continueLabel: "Let's test your memory!",
        },
      ],
    },

    // ═══════════════════════════════════════════════════════════
    // PHASE 4 — PIECE LETTER QUIZ (8 minutes)
    // ═══════════════════════════════════════════════════════════
    {
      id: 'letterquiz',
      title: 'Piece Letter Quiz',
      type: 'practice',
      durationMins: 8,
      steps: [
        {
          id: 'lq1',
          type: 'piece-spot-quiz',
          boardState: 'start',
          highlights: [],
          voice: `Time to test your memory, {name}! I'll highlight a piece on the board — you tell me which piece it is just from its shape. No letters yet — just recognise the piece by what it looks like!`,
          task: 'Which piece is on the highlighted square?',
          taskType: 'piece-spot-quiz',
          quizItems: [
            { square: 'e1', pieceName: 'King',   options: ['King', 'Queen', 'Bishop'] },
            { square: 'd1', pieceName: 'Queen',  options: ['King', 'Queen', 'Rook'] },
            { square: 'h1', pieceName: 'Rook',   options: ['Rook', 'Knight', 'Bishop'] },
            { square: 'c1', pieceName: 'Bishop', options: ['Bishop', 'Rook', 'Queen'] },
            { square: 'g1', pieceName: 'Knight', options: ['Bishop', 'Knight', 'King'] },
            { square: 'e2', pieceName: 'Pawn',   options: ['Pawn', 'Bishop', 'Rook'] },
          ],
          successVoice: `You know every piece by sight, {name}! Now let us check you know their letters too!`,
        },
        {
          id: 'lq2',
          type: 'piece-letter-quiz',
          boardState: 'start',
          highlights: [],
          voice: `Now the letters! I'll highlight a piece — you tell me its letter. Remember: K, Q, R, B, N — and Pawns have no letter at all!`,
          task: 'What letter does the highlighted piece use?',
          taskType: 'piece-letter-quiz',
          quizItems: [
            { square: 'g1', correctLetter: 'N', options: ['K', 'N', 'Kn'] },
            { square: 'd1', correctLetter: 'Q', options: ['Q', 'K', 'P'] },
            { square: 'a1', correctLetter: 'R', options: ['B', 'R', 'N'] },
            { square: 'f1', correctLetter: 'B', options: ['B', 'R', 'Q'] },
            { square: 'e1', correctLetter: 'K', options: ['Q', 'N', 'K'] },
            { square: 'e2', correctLetter: '(no letter)', options: ['P', '(no letter)', 'Pw'] },
          ],
          successVoice: `Outstanding, {name}! K for King, Q for Queen, R for Rook, B for Bishop, N for Knight, and no letter for Pawn. You have mastered the chess alphabet!`,
        },
      ],
    },

    // ═══════════════════════════════════════════════════════════
    // PHASE 5 — READING A MOVE (18 minutes, expanded from 10)
    // Minimum notation: piece letter + square = one move.
    // No captures, no check — those come in Lesson 24.
    // Every move spoken is shown on board + right panel.
    // ═══════════════════════════════════════════════════════════
    {
      id: 'readmove',
      title: 'Reading a Chess Move',
      type: 'teach',
      durationMins: 18,
      steps: [
        {
          id: 'rm1',
          type: 'observe',
          boardState: 'start',
          highlights: [],
          moveNotation: 'Re4 · Nf3 · Qd5 · e4',
          moveExplain: 'Examples — watch each one closely next!',
          voice: `{name}, now that you know every piece's letter, let's learn how to READ a chess move! A chess move is written as two things stuck together: the PIECE LETTER, then the SQUARE it moved to. That is all! "Re4" means the Rook moved to e4. "Nf3" means the Knight moved to f3. "Qd5" means the Queen moved to d5. If there is no letter at the front, a Pawn moved. So "e4" means a Pawn moved to e4!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'Let me see examples!',
        },
        {
          id: 'rm1b',
          type: 'observe',
          boardState: 'start',
          highlights: [],
          voice: `Let us slow this down even more, {name}. Every move has exactly TWO parts. Part one — the LETTER, which tells us WHICH piece moved. Part two — the SQUARE, which tells us WHERE it moved to. Letter first, square second, always stuck together with no space. Watch closely as I show you three real moves, one at a time!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'Show me the first one!',
        },
        {
          id: 'rm2',
          type: 'notation-demo',
          boardState: 'start',
          highlights: [],
          voice: `Watch this first move — "e4". No letter means a Pawn. The Pawn moves from e2 to e4 — two squares forward from its starting rank!`,
          task: null,
          taskType: 'notation-demo',
          moveFrom: 'e2',
          moveTo: 'e4',
          moveNotation: 'e4',
          moveExplain: 'No letter = Pawn moved to e4',
          demoFen: 'rnbqkbnr/pppppppp/8/8/4P3/8/PPPP1PPP/RNBQKBNR b KQkq - 0 1',
          path: ['e3', 'e4'],
          continueLabel: 'Next move',
        },
        {
          id: 'rm3',
          type: 'notation-demo',
          boardState: 'rnbqkbnr/pppppppp/8/8/4P3/8/PPPP1PPP/RNBQKBNR b KQkq - 0 1',
          highlights: [],
          voice: `Now "Nf3" — N means Knight. The White Knight jumps from g1 to f3 in its L-shape. Can you see it land on f3?`,
          task: null,
          taskType: 'notation-demo',
          moveFrom: 'g1',
          moveTo: 'f3',
          moveNotation: 'Nf3',
          moveExplain: 'N = Knight moved to f3',
          demoFen: 'rnbqkbnr/pppppppp/8/8/4P3/5N2/PPPP1PPP/RNBQKB1R b KQkq - 1 2',
          path: ['g2', 'g3', 'f3'],
          continueLabel: 'Next move',
        },
        {
          id: 'rm4',
          type: 'notation-demo',
          boardState: 'rnbqkbnr/pppppppp/8/8/4P3/5N2/PPPP1PPP/RNBQKB1R b KQkq - 1 2',
          highlights: [],
          voice: `And "Bc4" — B means Bishop. The White Bishop slides from f1 diagonally to c4. Bishop on c4, aiming at the centre! Three moves from the opening, all written in just six characters: e4, Nf3, Bc4.`,
          task: null,
          taskType: 'notation-demo',
          moveFrom: 'f1',
          moveTo: 'c4',
          moveNotation: 'Bc4',
          moveExplain: 'B = Bishop moved to c4',
          demoFen: 'rnbqkbnr/pppppppp/8/8/2B1P3/5N2/PPPP1PPP/RNBQK2R b KQkq - 2 3',
          path: ['e2', 'd3', 'c4'],
          continueLabel: 'Now try reading moves!',
        },
        {
          id: 'rm4b',
          type: 'observe',
          boardState: 'rnbqkbnr/pppppppp/8/8/2B1P3/5N2/PPPP1PPP/RNBQK2R b KQkq - 2 3',
          highlights: [],
          moveNotation: 'e4 Nf3 Bc4',
          moveExplain: 'Three moves: Pawn, Knight, Bishop',
          voice: `Look at the board now, {name} — this is what the position looks like after all three moves: e4, Nf3, Bc4. The Pawn moved forward, the Knight jumped out, and the Bishop is aiming at the centre. Every chess game ever played can be written down this way — letter plus square, move after move!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'Your turn to read moves!',
        },
        {
          id: 'rm5',
          type: 'observe',
          boardState: '8/8/8/8/8/8/8/R7 w - - 0 1',
          highlights: [],
          moveNotation: 'Rd1',
          moveExplain: 'R = Rook moved to d1',
          demoSequence: [
            { fen: '8/8/8/8/8/8/8/3R4 w - - 0 1', path: ['b1', 'c1', 'd1'], delay: 2200 },
          ],
          voice: `{name}, let us do a quick reading exercise. I will say a move and you tell me — which piece moved, and to which square? Try this one: "Rd1" — which piece? The Rook! To which square? d1!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'Next move!',
        },
        {
          id: 'rm5b',
          type: 'observe',
          boardState: '8/8/8/8/8/8/8/3Q4 w - - 0 1',
          highlights: [],
          moveNotation: 'Qd5',
          moveExplain: 'Q = Queen moved to d5',
          demoSequence: [
            { fen: '8/8/8/3Q4/8/8/8/8 w - - 0 1', path: ['d2','d3','d4','d5'], delay: 2200 },
          ],
          voice: `Next — "Qd5" — which piece? The Queen! She slides straight up the d-file to d5!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'Next move!',
        },
        {
          id: 'rm5c',
          type: 'observe',
          boardState: '8/8/8/8/8/8/8/1N6 w - - 0 1',
          highlights: [],
          moveNotation: 'Nc3',
          moveExplain: 'N = Knight moved to c3',
          demoSequence: [
            { fen: '8/8/8/8/8/2N5/8/8 w - - 0 1', path: ['b2','b3','c3'], delay: 2200 },
          ],
          voice: `Next — "Nc3" — which piece? The Knight! It jumps in its L-shape from b1 to c3!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'One more!',
        },
        {
          id: 'rm5d',
          type: 'observe',
          boardState: '8/8/8/8/8/8/3P4/8 w - - 0 1',
          highlights: [],
          moveNotation: 'd4',
          moveExplain: 'No letter = Pawn moved to d4',
          demoSequence: [
            { fen: '8/8/8/8/3P4/8/8/8 w - - 0 1', path: ['d3', 'd4'], delay: 2200 },
          ],
          voice: `And "d4" with no letter — which piece? The Pawn! To d4! You are reading chess notation already, {name}!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'Now a real practice round!',
        },

        // ── EXPANDED: guided practice quiz round, 6 new moves ──
        {
          id: 'rmq1',
          type: 'recap-quiz',
          boardState: '8/8/8/8/8/8/8/2B5 w - - 0 1',
          highlights: [],
          moveNotation: 'Bf4',
          moveExplain: 'Which piece moved, and to which square?',
          demoSequence: [
            { fen: '8/8/8/8/5B2/8/8/8 w - - 0 1', path: ['d2', 'e3', 'f4'], delay: 1800 },
          ],
          voice: `Practice round, {name}! Watch the board and read the move. "Bf4" — which piece moved?`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'Next move!',
          recapQuestion: 'Which piece does "Bf4" tell you moved?',
          recapOptions: ['The Bishop', 'The Rook', 'The Pawn'],
          recapCorrect: 0,
          recapCorrectVoice: `Yes! B means Bishop. The Bishop moved to f4. You read it perfectly, {name}!`,
          recapWrongVoice: `Remember — B is always Bishop. "Bf4" means the Bishop moved to the square f4.`,
        },
        {
          id: 'rmq2',
          type: 'recap-quiz',
          boardState: '8/8/8/8/8/8/8/R7 w - - 0 1',
          highlights: [],
          moveNotation: 'Ra4',
          moveExplain: 'Which square did the Rook move to?',
          demoSequence: [
            { fen: '8/8/8/8/R7/8/8/8 w - - 0 1', path: ['a2', 'a3', 'a4'], delay: 1800 },
          ],
          voice: `Next one — "Ra4". You already know R means Rook. But which SQUARE did it move to?`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'Next move!',
          recapQuestion: 'Which square does "Ra4" tell you the Rook moved to?',
          recapOptions: ['a4', 'a1', 'h4'],
          recapCorrect: 0,
          recapCorrectVoice: `Correct! The square comes right after the letter — Ra4 means the Rook moved to a4. Sharp reading, {name}!`,
          recapWrongVoice: `The square is written right after the letter. "Ra4" — the Rook moved to a4, not anywhere else!`,
        },
        {
          id: 'rmqsr1',
          type: 'recap-quiz',
          boardState: '8/8/8/8/8/8/8/R7 w - - 0 1',
          highlights: [],
          moveNotation: 'Ra5',
          moveExplain: 'R = Rook → moved to a5',
          demoSequence: [
            { fen: '8/8/8/R7/8/8/8/8 w - - 0 1', path: ['a2', 'a3', 'a4', 'a5'], delay: 1800 },
          ],
          voice: `Notation check, {name}! Watch the Rook slide up. What does "Ra5" tell you?`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'Next question!',
          recapQuestion: 'What does "Ra5" mean in chess notation?',
          recapOptions: ['The Rook moved to a5', 'The Rook moved to rank 5', 'A Pawn moved to a5'],
          recapCorrect: 0,
          recapCorrectVoice: `Correct! R is Rook, a5 is the square. Sharp reading, {name}!`,
          recapWrongVoice: `R is for Rook. "Ra5" = the Rook moved to the specific square a5 — not just "rank 5"!`,
        },
        {
          id: 'rmq3',
          type: 'recap-quiz',
          boardState: '8/8/8/8/8/8/8/1N6 w - - 0 1',
          highlights: [],
          moveNotation: 'Nc3',
          moveExplain: 'Which piece, and which square?',
          demoSequence: [
            { fen: '8/8/8/8/8/2N5/8/8 w - - 0 1', path: ['b2', 'b3', 'c3'], delay: 1800 },
          ],
          voice: `Here is a tricky one — "Nc3". Remember, N is NOT King. What piece does N stand for?`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'Next move!',
          recapQuestion: 'What piece does the letter "N" represent?',
          recapOptions: ['Knight', 'King', 'No piece — N means nothing'],
          recapCorrect: 0,
          recapCorrectVoice: `Exactly! N always means Knight, never King — because King already has the letter K. Nc3 means the Knight moved to c3!`,
          recapWrongVoice: `N stands for Knight! The King's letter is K — King already took it, so Knight uses N instead.`,
        },
        {
          id: 'rmq4',
          type: 'recap-quiz',
          boardState: '8/8/8/8/8/8/2P5/8 w - - 0 1',
          highlights: [],
          moveNotation: 'c4',
          moveExplain: 'No letter at the front — what does that mean?',
          demoSequence: [
            { fen: '8/8/8/8/2P5/8/8/8 w - - 0 1', path: ['c3', 'c4'], delay: 1800 },
          ],
          voice: `This move is written as just "c4" — no capital letter in front at all. What piece moved?`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'Next move!',
          recapQuestion: 'A move with NO letter in front, like "c4" — which piece moved?',
          recapOptions: ['A Pawn', 'A Knight', 'A King'],
          recapCorrect: 0,
          recapCorrectVoice: `Yes! No letter ALWAYS means Pawn. "c4" tells you a Pawn moved to the square c4. You have got this rule down perfectly, {name}!`,
          recapWrongVoice: `No letter in front always means a Pawn moved! Every other piece needs its letter — only the Pawn has none.`,
        },
        {
          id: 'rmqsr2',
          type: 'recap-quiz',
          boardState: '8/8/8/8/8/8/8/3N4 w - - 0 1',
          highlights: [],
          moveNotation: 'Ne3',
          moveExplain: 'N = Knight → moved to e3',
          demoSequence: [
            { fen: '8/8/8/8/8/4N3/8/8 w - - 0 1', path: ['d2', 'd3', 'e3'], delay: 1800 },
          ],
          voice: `Another notation check! The Knight jumps. What does "Ne3" tell you?`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'Next question!',
          recapQuestion: 'What does "Ne3" mean in chess notation?',
          recapOptions: ['The Knight moved to e3', 'The King moved to e3', 'The Knight moved to rank 3'],
          recapCorrect: 0,
          recapCorrectVoice: `Yes! N is always Knight — not King! Knight moved to e3, {name}!`,
          recapWrongVoice: `N stands for Knight, not King! "Ne3" means the Knight jumped to the square e3.`,
        },
        {
          id: 'rmq5',
          type: 'recap-quiz',
          boardState: '8/8/8/8/8/8/8/3Q4 w - - 0 1',
          highlights: [],
          moveNotation: 'Qh5',
          moveExplain: 'Which piece, and which square?',
          demoSequence: [
            { fen: '8/8/8/7Q/8/8/8/8 w - - 0 1', path: ['e2', 'f3', 'g4', 'h5'], delay: 1800 },
          ],
          voice: `One more — "Qh5". This is a famous opening move some chess players love! Which piece moved, and where?`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'Final move!',
          recapQuestion: 'What does "Qh5" tell you?',
          recapOptions: ['The Queen moved to h5', 'The King moved to h5', 'A Pawn moved to h5'],
          recapCorrect: 0,
          recapCorrectVoice: `Perfect! Q is always Queen. "Qh5" means the Queen slid all the way to h5. You are reading chess like a real player, {name}!`,
          recapWrongVoice: `Q always means Queen — never King, never Pawn. "Qh5" means the Queen moved to h5.`,
        },
        {
          id: 'rmq6',
          type: 'recap-quiz',
          boardState: '8/8/8/8/8/8/8/4K2R w K - 0 1',
          highlights: [],
          moveNotation: 'Kf2',
          moveExplain: 'Which piece, and which square?',
          demoSequence: [
            { fen: '8/8/8/8/8/8/5K2/7R w - - 0 1', path: ['f2'], delay: 1800 },
          ],
          voice: `Last one in this round — "Kf2". Careful, {name} — do not confuse this with Knight!`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'One final challenge!',
          recapQuestion: 'What does "Kf2" tell you?',
          recapOptions: ['The King moved to f2', 'The Knight moved to f2', 'The Bishop moved to f2'],
          recapCorrect: 0,
          recapCorrectVoice: `Excellent, {name}! Capital K is always King. "Kf2" means the King carefully stepped to f2 — just one square, as Kings always do!`,
          recapWrongVoice: `Capital K is the King's letter — Knight uses N instead, remember? "Kf2" means the King moved to f2.`,
        },
        {
          id: 'rmqsr3',
          type: 'recap-quiz',
          boardState: '8/8/8/8/8/8/8/6B1 w - - 0 1',
          highlights: [],
          moveNotation: 'Bd4',
          moveExplain: 'B = Bishop → moved to d4',
          demoSequence: [
            { fen: '8/8/8/8/3B4/8/8/8 w - - 0 1', path: ['f2', 'e3', 'd4'], delay: 1800 },
          ],
          voice: `One more notation check! What does "Bd4" mean?`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'Next!',
          recapQuestion: 'What does "Bd4" mean in chess notation?',
          recapOptions: ['The Bishop moved to d4', 'The Bishop moved to file d', 'A Pawn moved to d4'],
          recapCorrect: 0,
          recapCorrectVoice: `Correct! B is Bishop, d4 is the square. "Bd4" — Bishop to d4, {name}!`,
          recapWrongVoice: `B is for Bishop. "Bd4" means the Bishop slid diagonally to the specific square d4.`,
        },

        {
          id: 'rm6',
          type: 'recap-quiz',
          boardState: '8/8/8/8/1N6/8/8/8 w - - 0 1',
          highlights: [],
          moveNotation: 'Nc6',
          moveExplain: 'Which piece, and which square?',
          demoSequence: [
            { fen: '8/8/2N5/8/8/8/8/8 w - - 0 1', path: ['b5','b6','c6'], delay: 1800 },
          ],
          voice: `One final quiz before we move on — what does the move "Nc6" tell you?`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'One more thing before we finish...',
          recapQuestion: 'What does the chess move "Nc6" mean?',
          recapOptions: [
            'A Knight moved to the square c6',
            'A pawn moved to c6',
            'The King moved to c6',
          ],
          recapCorrect: 0,
          recapCorrectVoice: `Yes! N means Knight, and c6 is the destination square. A Knight moved to c6. You can read chess notation, {name}!`,
          recapWrongVoice: `Remember — N is the letter for Knight. So "Nc6" means a Knight moved to the square c6. Never a Pawn — Pawns have no letter!`,
        },

        // ── FIX 2 — Rule 32: notation-drag exercise (read AND move) ──
        {
          id: 'nd1',
          taskType: 'notation-drag',
          voice: `Now YOU play the moves, {name}! I will call a chess move — read the notation on the right, then drag the piece on the board to the correct square. Piece letter tells you WHICH piece. The square tells you WHERE. Ready to drag?`,
          task: 'Read the notation on the right, then drag the piece to that square.',
          notationCalls: [
            {
              id: 'ndc1',
              notation: 'Ra4',
              explain: 'R = Rook → drag to a4',
              boardState: '8/8/8/8/8/8/8/R7 w - - 0 1',
              pieceSquare: 'a1',
              targetSquare: 'a4',
              voice: `Ra4! R means Rook. Drag the Rook up to a4!`,
            },
            {
              id: 'ndc2',
              notation: 'Nf3',
              explain: 'N = Knight → drag to f3',
              boardState: '8/8/8/8/8/8/8/6N1 w - - 0 1',
              pieceSquare: 'g1',
              targetSquare: 'f3',
              voice: `Nf3! N means Knight. Drag the Knight to f3!`,
            },
            {
              id: 'ndc3',
              notation: 'Bc4',
              explain: 'B = Bishop → drag to c4',
              boardState: '8/8/8/8/8/8/8/5B2 w - - 0 1',
              pieceSquare: 'f1',
              targetSquare: 'c4',
              voice: `Bc4! B means Bishop. Drag the Bishop diagonally to c4!`,
            },
            {
              id: 'ndc4',
              notation: 'Qh5',
              explain: 'Q = Queen → drag to h5',
              boardState: '8/8/8/8/8/8/8/3Q4 w - - 0 1',
              pieceSquare: 'd1',
              targetSquare: 'h5',
              voice: `Qh5! Q means Queen. Drag the Queen all the way to h5!`,
            },
          ],
          pieceLetterRef: [
            { icon: '♖', letter: 'R', name: 'Rook' },
            { icon: '♘', letter: 'N', name: 'Knight' },
            { icon: '♗', letter: 'B', name: 'Bishop' },
            { icon: '♕', letter: 'Q', name: 'Queen' },
          ],
          successVoice: `OUTSTANDING, {name}! You read the notation AND dragged each piece to the correct square. Letter tells you the piece — square tells you where. You are already playing chess!`,
        },
      ],
    },

    // ═══════════════════════════════════════════════════════════
    // PHASE 6 — STARTING POSITION REVISION (9 minutes, expanded)
    // Know where every piece starts — sets up Lesson 6 (board setup)
    // Each step now shows its pieceLetterRef so the letter spoken
    // is also seen, matching the rule from Phase 3.
    // ═══════════════════════════════════════════════════════════
    {
      id: 'startpos',
      title: 'Where Every Piece Starts',
      type: 'teach',
      durationMins: 9,
      steps: [
        {
          id: 'sp0',
          type: 'observe',
          boardState: 'start',
          highlights: [],
          voice: `{name}, before we finish today, let us look at the WHOLE starting position one more time and lock in everything you have learned — every piece, every letter, every starting square. This is the picture you will see at the start of every single chess game you ever play!`,
          task: null,
          taskType: 'observe',
          continueLabel: "Let's go!",
        },
        // ── Rule 33: piece-placement — all 32 pieces, both sides ──
        {
          id: 'sp_pp',
          taskType: 'piece-placement',
          boardState: 'empty',
          voice: `Now YOU build the board, {name}! White pieces are in the left palette. Black pieces are on the right. I will call each piece and its square — drag it from the palette onto the board. Let us start! White Rook — drag it to a1!`,
          task: 'Drag each piece from the side palette to its correct starting square.',
          placementItems: [
            // White back rank (left-to-right a1→h1)
            { id:'wR1', piece:'R', color:'w', square:'a1', icon:'♖', callVoice:'White Rook — a1!' },
            { id:'wN1', piece:'N', color:'w', square:'b1', icon:'♘', callVoice:'White Knight — b1!' },
            { id:'wB1', piece:'B', color:'w', square:'c1', icon:'♗', callVoice:'White Bishop — c1, dark square!' },
            { id:'wQ',  piece:'Q', color:'w', square:'d1', icon:'♕', callVoice:'White Queen — d1, her own colour!' },
            { id:'wK',  piece:'K', color:'w', square:'e1', icon:'♔', callVoice:'White King — e1, beside the Queen!' },
            { id:'wB2', piece:'B', color:'w', square:'f1', icon:'♗', callVoice:'Second White Bishop — f1, light square!' },
            { id:'wN2', piece:'N', color:'w', square:'g1', icon:'♘', callVoice:'Second White Knight — g1!' },
            { id:'wR2', piece:'R', color:'w', square:'h1', icon:'♖', callVoice:'Second White Rook — h1, other corner!' },
            // White pawns (a2→h2)
            { id:'wPa', piece:'P', color:'w', square:'a2', icon:'♙', callVoice:'White Pawn — a2!' },
            { id:'wPb', piece:'P', color:'w', square:'b2', icon:'♙', callVoice:'White Pawn — b2!' },
            { id:'wPc', piece:'P', color:'w', square:'c2', icon:'♙', callVoice:'White Pawn — c2!' },
            { id:'wPd', piece:'P', color:'w', square:'d2', icon:'♙', callVoice:'White Pawn — d2!' },
            { id:'wPe', piece:'P', color:'w', square:'e2', icon:'♙', callVoice:'White Pawn — e2!' },
            { id:'wPf', piece:'P', color:'w', square:'f2', icon:'♙', callVoice:'White Pawn — f2!' },
            { id:'wPg', piece:'P', color:'w', square:'g2', icon:'♙', callVoice:'White Pawn — g2!' },
            { id:'wPh', piece:'P', color:'w', square:'h2', icon:'♙', callVoice:'Last White Pawn — h2! Now the Black pieces!' },
            // Black back rank (a8→h8)
            { id:'bR1', piece:'R', color:'b', square:'a8', icon:'♜', callVoice:'Black Rook — a8, top-left corner!' },
            { id:'bN1', piece:'N', color:'b', square:'b8', icon:'♞', callVoice:'Black Knight — b8!' },
            { id:'bB1', piece:'B', color:'b', square:'c8', icon:'♝', callVoice:'Black Bishop — c8!' },
            { id:'bQ',  piece:'Q', color:'b', square:'d8', icon:'♛', callVoice:'Black Queen — d8!' },
            { id:'bK',  piece:'K', color:'b', square:'e8', icon:'♚', callVoice:'Black King — e8!' },
            { id:'bB2', piece:'B', color:'b', square:'f8', icon:'♝', callVoice:'Second Black Bishop — f8!' },
            { id:'bN2', piece:'N', color:'b', square:'g8', icon:'♞', callVoice:'Second Black Knight — g8!' },
            { id:'bR2', piece:'R', color:'b', square:'h8', icon:'♜', callVoice:'Second Black Rook — h8!' },
            // Black pawns (a7→h7)
            { id:'bPa', piece:'P', color:'b', square:'a7', icon:'♟', callVoice:'Black Pawn — a7!' },
            { id:'bPb', piece:'P', color:'b', square:'b7', icon:'♟', callVoice:'Black Pawn — b7!' },
            { id:'bPc', piece:'P', color:'b', square:'c7', icon:'♟', callVoice:'Black Pawn — c7!' },
            { id:'bPd', piece:'P', color:'b', square:'d7', icon:'♟', callVoice:'Black Pawn — d7!' },
            { id:'bPe', piece:'P', color:'b', square:'e7', icon:'♟', callVoice:'Black Pawn — e7!' },
            { id:'bPf', piece:'P', color:'b', square:'f7', icon:'♟', callVoice:'Black Pawn — f7!' },
            { id:'bPg', piece:'P', color:'b', square:'g7', icon:'♟', callVoice:'Black Pawn — g7!' },
            { id:'bPh', piece:'P', color:'b', square:'h7', icon:'♟', callVoice:'{name}, you did it! All 32 pieces on the board!' },
          ],
          successVoice: `Incredible, {name}! You placed all 32 chess pieces on their correct starting squares. You know exactly where every piece lives on the board. That is the whole army!`,
        },

        // ── Rule 35: unguided piece-placement — all pieces active, no hints ──
        {
          id: 'sp_ppu',
          taskType: 'piece-placement-unguided',
          boardState: 'empty',
          voice: `Now it is YOUR turn, {name} — completely from memory this time! All the pieces are ready in the palette, but I will not tell you which one to use, and nothing will glow. Board is empty again. First challenge: put the white Queen on her starting square!`,
          task: 'Place each piece Ms. Momo calls — no highlights, no hints. Work from memory!',
          placementItems: [
            { id:'wR1', piece:'R', color:'w', square:'a1', icon:'♖' },
            { id:'wN1', piece:'N', color:'w', square:'b1', icon:'♘' },
            { id:'wB1', piece:'B', color:'w', square:'c1', icon:'♗' },
            { id:'wQ',  piece:'Q', color:'w', square:'d1', icon:'♕' },
            { id:'wK',  piece:'K', color:'w', square:'e1', icon:'♔' },
            { id:'wB2', piece:'B', color:'w', square:'f1', icon:'♗' },
            { id:'wN2', piece:'N', color:'w', square:'g1', icon:'♘' },
            { id:'wR2', piece:'R', color:'w', square:'h1', icon:'♖' },
            { id:'wPa', piece:'P', color:'w', square:'a2', icon:'♙' },
            { id:'wPb', piece:'P', color:'w', square:'b2', icon:'♙' },
            { id:'wPc', piece:'P', color:'w', square:'c2', icon:'♙' },
            { id:'wPd', piece:'P', color:'w', square:'d2', icon:'♙' },
            { id:'wPe', piece:'P', color:'w', square:'e2', icon:'♙' },
            { id:'wPf', piece:'P', color:'w', square:'f2', icon:'♙' },
            { id:'wPg', piece:'P', color:'w', square:'g2', icon:'♙' },
            { id:'wPh', piece:'P', color:'w', square:'h2', icon:'♙' },
            { id:'bR1', piece:'R', color:'b', square:'a8', icon:'♜' },
            { id:'bN1', piece:'N', color:'b', square:'b8', icon:'♞' },
            { id:'bB1', piece:'B', color:'b', square:'c8', icon:'♝' },
            { id:'bQ',  piece:'Q', color:'b', square:'d8', icon:'♛' },
            { id:'bK',  piece:'K', color:'b', square:'e8', icon:'♚' },
            { id:'bB2', piece:'B', color:'b', square:'f8', icon:'♝' },
            { id:'bN2', piece:'N', color:'b', square:'g8', icon:'♞' },
            { id:'bR2', piece:'R', color:'b', square:'h8', icon:'♜' },
            { id:'bPa', piece:'P', color:'b', square:'a7', icon:'♟' },
            { id:'bPb', piece:'P', color:'b', square:'b7', icon:'♟' },
            { id:'bPc', piece:'P', color:'b', square:'c7', icon:'♟' },
            { id:'bPd', piece:'P', color:'b', square:'d7', icon:'♟' },
            { id:'bPe', piece:'P', color:'b', square:'e7', icon:'♟' },
            { id:'bPf', piece:'P', color:'b', square:'f7', icon:'♟' },
            { id:'bPg', piece:'P', color:'b', square:'g7', icon:'♟' },
            { id:'bPh', piece:'P', color:'b', square:'h7', icon:'♟' },
          ],
          unguidedChallenges: [
            { id:'u1', piece:'Q', color:'w', square:'d1', prompt: 'Put the white Queen on her starting square!' },
            { id:'u2', piece:'R', color:'w', square:'a1', prompt: 'Which piece belongs on a1? Place it there!' },
            { id:'u3', piece:'N', color:'b', square:'b8', prompt: 'Place one of the black Knights on its correct square!' },
            { id:'u4', piece:'N', color:'b', square:'g8', prompt: 'Now place the OTHER black Knight!' },
            { id:'u5', piece:'B', color:'w', square:'c1', prompt: 'Put the white Bishop on its dark starting square!' },
            { id:'u6', piece:'K', color:'w', square:'e1', prompt: 'Place the white King on his starting square!' },
          ],
          wrongVoice: `Not quite — think about where that piece starts!`,
          successVoice: `Incredible, {name}! You placed every piece completely from memory — no hints needed. You truly know the whole starting position!`,
        },

        // ── EXPANDED: starting square recall quiz, 3 new questions ──
        {
          id: 'sp6',
          type: 'recap-quiz',
          boardState: 'start',
          highlights: [],
          voice: `Quick quiz, {name}! Where do the WHITE Bishops start on the board?`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'Next question!',
          recapQuestion: 'Where do the WHITE Bishops start?',
          recapOptions: ['c1 and f1', 'a1 and h1', 'b1 and g1'],
          recapCorrect: 0,
          recapCorrectVoice: `Yes! The White Bishops start on c1 and f1 — right next to the Knights. Great memory, {name}!`,
          recapWrongVoice: `The White Bishops start on c1 and f1. Rooks are in the corners, Knights are next to them, then Bishops!`,
        },
        {
          id: 'spsr1',
          type: 'recap-quiz',
          boardState: 'start',
          highlights: [],
          pieceLetterRef: [{ icon: '♖', letter: 'R', name: 'Rook' }],
          voice: `Notation recap, {name}! You have seen K, Q, R, B, N on the right panel. Which piece uses the letter R?`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'Two more questions!',
          recapQuestion: 'Which chess piece uses the letter R in chess notation?',
          recapOptions: ['The Rook', 'The Rank', 'The Runner'],
          recapCorrect: 0,
          recapCorrectVoice: `R for Rook! Rooks start in the four corners — a1, h1, a8, h8. Easy to spot, {name}!`,
          recapWrongVoice: `R stands for Rook! The Rooks sit in the corners: a1, h1, a8, h8.`,
        },
        {
          id: 'sp7',
          type: 'recap-quiz',
          boardState: 'start',
          highlights: [],
          voice: `Next — which White piece starts on d1?`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'One more!',
          recapQuestion: 'Which White piece starts on d1?',
          recapOptions: ['The Queen', 'The King', 'The Rook'],
          recapCorrect: 0,
          recapCorrectVoice: `Correct! The Queen always starts on d1 for White and d8 for Black — on her own colour. Well done, {name}!`,
          recapWrongVoice: `It is the QUEEN that starts on d1. Remember — the Queen starts on her own colour square!`,
        },
        {
          id: 'sp8',
          type: 'recap-quiz',
          boardState: 'start',
          highlights: [],
          voice: `Last one — how many White Pawns are there, and which rank do they start on?`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'One more thing before we finish...',
          recapQuestion: 'Where do the White Pawns start, and how many are there?',
          recapOptions: [
            'Eight Pawns on rank 2',
            'Six Pawns on rank 1',
            'Eight Pawns on rank 3',
          ],
          recapCorrect: 0,
          recapCorrectVoice: `Yes! White has eight Pawns, all on rank 2 — one per file. Black's eight Pawns mirror them on rank 7. You know the entire starting position now, {name}!`,
          recapWrongVoice: `White has EIGHT Pawns on rank 2 — one on every file from a to h. They stand like a wall in front of the other pieces.`,
        },
      ],
    },

    // ═══════════════════════════════════════════════════════════
    // PHASE 7 — WRAP-UP (5 minutes)
    // ═══════════════════════════════════════════════════════════
    {
      id: 'wrapup',
      title: 'Lesson Complete!',
      type: 'wrapup',
      durationMins: 5,
      steps: [
        {
          id: 'wu1',
          type: 'wrapup',
          boardState: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
          highlights: [],
          pieceLetterRef: [
            { icon: '♔', letter: 'K', name: 'King' },
            { icon: '♕', letter: 'Q', name: 'Queen' },
            { icon: '♖', letter: 'R', name: 'Rook' },
            { icon: '♗', letter: 'B', name: 'Bishop' },
            { icon: '♘', letter: 'N', name: 'Knight' },
            { icon: '♙', letter: '—', name: 'Pawn (no letter)' },
          ],
          voice: `{name}, you have done something real today! You now know every chess piece by shape, by name, by letter, and by starting square. K for King, Q for Queen, R for Rook, B for Bishop, N for Knight, and Pawns have no letter. You can read a simple chess move — piece letter plus square equals a move. And you know where every single piece begins on the board. That is the foundation of everything we will build in the coming lessons!`,
          task: null,
          taskType: 'recap-quiz',
          continueLabel: 'One more thing before we finish...',
          recapQuestion: 'Which piece has NO letter in chess notation?',
          recapOptions: ['The Pawn', 'The Knight', 'The Bishop'],
          recapCorrect: 0,
          recapCorrectVoice: `Exactly right! The Pawn has no letter — so any move without a capital letter at the front is ALWAYS a Pawn. You have learned this well, {name}!`,
          recapWrongVoice: `It is the PAWN that has no letter! K for King, Q for Queen, R for Rook, B for Bishop, N for Knight — and Pawns have none. Any move without a letter is a Pawn move.`,
        },
        {
          id: 'wu1b',
          type: 'wrapup',
          boardState: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
          highlights: [],
          pieceLetterRef: [{ icon: '♔', letter: 'K', name: 'King' },{ icon: '♘', letter: 'N', name: 'Knight' }],
          voice: `Quick question. Which letter represents the Knight in chess notation?`,
          task: null,
          taskType: 'recap-quiz',
          recapQuestion: 'What is the letter for the Knight?',
          recapOptions: ['N','K','Kn','Kt'],
          recapCorrect: 0,
          recapCorrectVoice: `N for Knight. Not K — that is the King. N for Knight. Every time.`,
          recapWrongVoice: `It is N for Knight. K is already taken by the King. So the Knight gets N. Remember that.`,
        },
        {
          id: 'wu1c',
          type: 'wrapup',
          boardState: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
          highlights: ['d1','d8'],
          pieceLetterRef: [{ icon: '♕', letter: 'Q', name: 'Queen' }],
          voice: `Last one. What letter does the Queen use in chess notation?`,
          task: null,
          taskType: 'recap-quiz',
          recapQuestion: 'What is the letter for the Queen?',
          recapOptions: ['Q','Qu','Qn','W'],
          recapCorrect: 0,
          recapCorrectVoice: `Q for Queen. Simple. Q for Queen. Always.`,
          recapWrongVoice: `Q for Queen. Just the letter Q. Always.`,
        },
        {
          id: 'wu2a',
          type: 'wrapup',
          boardState: '8/8/8/8/4R3/8/8/8 w - - 0 1',
          highlights: [],
          pieceLetterRef: [{ icon: '♖', letter: 'R', name: 'Rook' }],
          demoSequence: [
            { fen: '8/8/8/8/7R/8/8/8 w - - 0 1', path: ['f4','g4','h4'], delay: 2400 },
          ],
          voice: `Next time, {name}, we discover HOW the Rook actually moves! Watch it slide straight RIGHT along rank 4 — pure straight lines in any of four directions. That is the Rook's power, coming to life in Lesson 3!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'Now the Bishop!',
        },
        {
          id: 'wu2b',
          type: 'wrapup',
          boardState: '8/8/8/8/8/8/8/2B5 w - - 0 1',
          highlights: [],
          pieceLetterRef: [{ icon: '♗', letter: 'B', name: 'Bishop' }],
          demoSequence: [
            { fen: '8/8/7B/8/8/8/8/8 w - - 0 1', path: ['d2','e3','f4','g5','h6'], delay: 2600 },
          ],
          voice: `And the Bishop moves like THIS — a long diagonal from c1 all the way to h6! Four diagonal directions, pure diagonals only. The Bishop even has a colour secret you will find amazing in Lesson 3!`,
          task: null,
          taskType: 'observe',
          continueLabel: 'See you next time!',
        },
        {
          id: 'wu2c',
          type: 'wrapup',
          boardState: 'r1b2b1r/8/8/8/8/8/8/R1B2B1R w - - 0 1',
          highlights: ['a1','h1','a8','h8','c1','f1','c8','f8'],
          pieceLetterRef: [
            { icon: '♖', letter: 'R', name: 'Rook' },
            { icon: '♗', letter: 'B', name: 'Bishop' },
          ],
          voice: `{name}, you have done something brilliant today! You now know the letter for every chess piece — K for King, Q for Queen, R for Rook, B for Bishop, N for Knight, and no letter at all for the Pawn. You can read a chess move — letter first, then the square, like "Nc3" or "Qh5". And you know the starting square of every single piece on the board. That is the complete foundation of Lesson 2. See you in Lesson 3! Now let us put everything to the test in your bonus round — let us see how much you remember!`,
          task: null,
          taskType: 'complete',
          continueLabel: 'Finish lesson!',
        },
      ],
    },

  ],
};

export const LESSON_2_BONUS = {
  title: 'Bonus Round — Piece Letters and Chess Notation!',
  phases: [
    {
      id: 'bonus1',
      title: 'Chess Notation Trivia',
      durationMins: 8,
      steps: [
        {
          id: 'b2q1',
          taskType: 'recap-quiz',
          boardState: 'empty',
          highlights: [],
          pieceLetterRef: [
            { icon: '♘', letter: 'N', name: 'Knight' },
            { icon: '♔', letter: 'K', name: 'King' },
          ],
          voice: `Bonus round time, {name}! You finished the lesson so fast — now let us see what you remember. Which letter does the Knight use in chess notation?`,
          recapQuestion: 'Which letter does the Knight use in chess notation?',
          recapOptions: ['N', 'K', 'Kn'],
          recapCorrect: 0,
          recapCorrectVoice: `Yes — N for Knight! K is already taken by the King, so the Knight uses N instead. Great recall, {name}!`,
          recapWrongVoice: `N is for Knight! The King already uses K, so the Knight had to take N. Remember — N, not K!`,
          continueLabel: 'Next question!',
        },
        {
          id: 'b2q2',
          taskType: 'recap-quiz',
          boardState: 'empty',
          highlights: [],
          pieceLetterRef: [
            { icon: '♘', letter: 'N', name: 'Knight' },
            { icon: '♔', letter: 'K', name: 'King' },
          ],
          voice: `A move is written as just "d4" — no capital letter in front at all. Which piece moved?`,
          recapQuestion: 'A move written as "d4" with no capital letter — which piece moved?',
          recapOptions: ['A Pawn', 'A Knight', 'A King'],
          recapCorrect: 0,
          recapCorrectVoice: `Correct! No letter always means Pawn. Knights need N and Kings need K — only Pawns have no letter!`,
          recapWrongVoice: `No letter in front ALWAYS means a Pawn moved! Every other piece needs its letter — only the Pawn has none.`,
          continueLabel: 'Next question!',
        },
        {
          id: 'b2sr1',
          taskType: 'speed-round',
          boardState: 'empty',
          voice: `Speed break! Find every square I call as fast as you can!`,
          timeLimitSecs: 30,
          targetCount: 8,
          successVoice: `Great speed, {name}! Back to the questions!`,
          completeVoice: `Time is up — {score} squares found! Back to the questions!`,
        },
        {
          id: 'b2q3',
          taskType: 'recap-quiz',
          boardState: '8/8/8/8/5B2/8/8/8 w - - 0 1',
          highlights: [],
          pieceLetterRef: [
            { icon: '♗', letter: 'B', name: 'Bishop' },
            { icon: '♔', letter: 'K', name: 'King' },
          ],
          voice: `Look at the board — a Bishop just moved. The notation reads "Bf4". What does that tell you?`,
          recapQuestion: 'What does "Bf4" tell you in chess notation?',
          recapOptions: ['The Bishop moved to f4', 'The King moved to f4', 'A Pawn moved to f4'],
          recapCorrect: 0,
          recapCorrectVoice: `Exactly! B means Bishop, f4 is the square. The Bishop moved to f4. You are reading chess notation perfectly, {name}!`,
          recapWrongVoice: `B is for Bishop — so Bf4 means the Bishop moved to f4. K would mean King, and no letter would mean Pawn.`,
          continueLabel: 'Next question!',
        },
        {
          id: 'b2q4',
          taskType: 'recap-quiz',
          boardState: '8/8/8/3b4/8/8/8/8 w - - 0 1',
          highlights: [],
          pieceLetterRef: [
            { icon: '♗', letter: 'B', name: 'Bishop' },
          ],
          voice: `The Bishop is right in the centre of the board. How many squares can it control from there?`,
          recapQuestion: 'How many squares does a Bishop control from the centre?',
          recapOptions: ['Eight', 'Thirteen', 'Fourteen'],
          recapCorrect: 1,
          recapCorrectVoice: `Thirteen! The Bishop spreads diagonally in four directions from the centre — counting thirteen squares in total. Sharp memory, {name}!`,
          recapWrongVoice: `Thirteen squares! The Bishop reaches diagonally in four directions — seven on the longest diagonals, fewer on shorter ones, totalling thirteen.`,
          continueLabel: 'Next question!',
        },
        {
          id: 'b2sr2',
          taskType: 'speed-round',
          boardState: 'empty',
          voice: `Mid-trivia speed break — find every square I call!`,
          timeLimitSecs: 30,
          targetCount: 8,
          successVoice: `Lightning fast, {name}! Back to the questions!`,
          completeVoice: `Time is up — {score} squares found! Back to the questions!`,
        },
        {
          id: 'b2q5',
          taskType: 'recap-quiz',
          boardState: 'empty',
          highlights: [],
          pieceLetterRef: [
            { icon: '♖', letter: 'R', name: 'Rook' },
          ],
          voice: `Here is a movement question. The Rook — can it ever move diagonally?`,
          recapQuestion: 'Can the Rook ever move diagonally?',
          recapOptions: ['No — straight lines only', 'Yes — in all directions', 'Only one step diagonally'],
          recapCorrect: 0,
          recapCorrectVoice: `Correct — the Rook ONLY moves in straight lines! Up, down, left, right. Diagonals belong to the Bishop!`,
          recapWrongVoice: `No — the Rook never moves diagonally! It slides only in straight lines. Diagonals are strictly the Bishop's territory.`,
          continueLabel: 'Next question!',
        },
        {
          id: 'b2q6',
          taskType: 'recap-quiz',
          boardState: 'start',
          highlights: [],
          pieceLetterRef: [
            { icon: '♕', letter: 'Q', name: 'Queen' },
          ],
          voice: `Look at the starting position. Which square does the White Queen begin on?`,
          recapQuestion: 'Which square does the White Queen start on?',
          recapOptions: ['e1', 'd1', 'c1'],
          recapCorrect: 1,
          recapCorrectVoice: `d1! The Queen always starts on d1 for White — on her own colour. The King takes e1. You know the whole back row, {name}!`,
          recapWrongVoice: `The White Queen starts on d1 — always on her own colour square. The King is on e1 right next to her.`,
          continueLabel: 'Next question!',
        },
        {
          id: 'b2sr3',
          taskType: 'speed-round',
          boardState: 'empty',
          voice: `Another speed break — find every square I call!`,
          timeLimitSecs: 30,
          targetCount: 8,
          successVoice: `Excellent speed, {name}! Two more questions to go!`,
          completeVoice: `Time is up — {score} squares found! Two more questions to go!`,
        },
        {
          id: 'b2q7',
          taskType: 'recap-quiz',
          boardState: 'empty',
          highlights: [],
          pieceLetterRef: [
            { icon: '♕', letter: 'Q', name: 'Queen' },
            { icon: '♔', letter: 'K', name: 'King' },
          ],
          voice: `Which letter does the Queen use in chess notation — and remember, it is NOT the same as the King!`,
          recapQuestion: 'Which letter represents the Queen in chess notation?',
          recapOptions: ['K', 'Q', 'Qu'],
          recapCorrect: 1,
          recapCorrectVoice: `Q for Queen! K is the King's letter — the Queen gets Q. Never mix them up!`,
          recapWrongVoice: `Q is for Queen! K belongs to the King. Always Q for Queen, K for King.`,
          continueLabel: 'Last question!',
        },
        {
          id: 'b2q8',
          taskType: 'recap-quiz',
          boardState: '8/8/8/8/8/8/8/2B5 w - - 0 1',
          highlights: [],
          pieceLetterRef: [
            { icon: '♗', letter: 'B', name: 'Bishop' },
          ],
          voice: `This Bishop starts on c1 — a dark square. What colour squares can it visit for the REST of the game?`,
          recapQuestion: 'A Bishop that starts on a dark square — what colour squares can it visit?',
          recapOptions: ['Light and dark squares', 'Only light squares', 'Only dark squares'],
          recapCorrect: 2,
          recapCorrectVoice: `Only dark squares — FOREVER! The Bishop is colour-locked from the very first move. Once dark, always dark. Outstanding, {name}!`,
          recapWrongVoice: `Only dark squares — for the entire game! The Bishop's colour-lock is permanent. The c1 Bishop will never, ever visit a light square.`,
          continueLabel: 'Now — Speed Challenge!',
        },
      ],
    },
    {
      id: 'bonus2',
      title: 'Speed Square Challenge',
      durationMins: 6,
      steps: [
        {
          id: 'b2si1',
          taskType: 'speed-intro',
          boardState: 'empty',
          voice: `Now the SPEED SQUARE CHALLENGE, {name}! I will call squares and you click them as fast as you can. Two rounds — can you beat your own score? Ready? GO!`,
          continueLabel: 'Start speed challenge!',
        },
        {
          id: 'b2s1',
          taskType: 'speed-round',
          boardState: 'empty',
          voice: `Round 1 — find every square I call!`,
          timeLimitSecs: 90,
          targetCount: 20,
          successVoice: `Incredible! You found {score} squares in {time} seconds! You know this board, {name}!`,
          completeVoice: `Time is up — {score} squares found! Amazing effort, {name}!`,
        },
        {
          id: 'b2s2',
          taskType: 'speed-round',
          boardState: 'empty',
          voice: `Round 2 — different squares! Can you beat round 1?`,
          timeLimitSecs: 90,
          targetCount: 20,
          successVoice: `OUTSTANDING! {score} squares in round 2! Speed is becoming your superpower, {name}!`,
          completeVoice: `Time is up — {score} squares in round 2! You are getting faster every time, {name}!`,
        },
      ],
    },
    {
      id: 'bonus3',
      title: 'Bonus Complete',
      durationMins: 1,
      steps: [
        {
          id: 'b2end',
          taskType: 'complete',
          boardState: 'empty',
          voice: `{name}, you have been absolutely BRILLIANT today! You learned the letter for every chess piece — K for King, Q for Queen, R for Rook, B for Bishop, N for Knight, and no letter for the Pawn. You read chess notation, discovered how the Rook and Bishop move, and memorised every starting square on the board. Lesson 2 is completely yours. See you in Lesson 3!`,
          continueLabel: 'Finish Lesson 2!',
        },
      ],
    },
  ],
};
